import { useMutation } from '@apollo/client'
import omit from 'lodash/omit'

import { IconName, PairedColour } from '@acre/design-system'
import { AddNoteDocument, GetNotesDocument, omitTypename, UpdateNoteDocument } from '@acre/graphql'
import { ErrorProvider } from '@acre/utils'

const noteTitlesPrefix = 'cases.notes.noteTitles'

export enum FENoteType {
  General = 'General',
  Calendar = 'Calendar',
  Sms = 'Sms',
  Email = 'Email',
  Call = 'Call',
  Messaging = 'Messaging',
}

export type Recipient = {
  id: string
  name: string
  email: string
}

export const getNoteTypeDisplayDetails = (noteType?: Maybe<FENoteType>) => {
  switch (noteType) {
    case FENoteType.General:
      return { iconName: IconName.Edit, iconColour: PairedColour.Purple, title: `${noteTitlesPrefix}.generalNote` }
    case FENoteType.Calendar:
      return { iconName: IconName.Calendar, iconColour: PairedColour.Turquoise, title: `${noteTitlesPrefix}.calendar` }
    case FENoteType.Sms:
      return { iconName: IconName.Smartphone, iconColour: PairedColour.Violet, title: `${noteTitlesPrefix}.sms` }
    case FENoteType.Email:
      return { iconName: IconName.Mail, iconColour: PairedColour.Yellow, title: `${noteTitlesPrefix}.email` }
    case FENoteType.Call:
      return { iconName: IconName.Phone, iconColour: PairedColour.Mint, title: `${noteTitlesPrefix}.call` }
    case FENoteType.Messaging:
      return {
        iconName: IconName.MessageCircle,
        iconColour: PairedColour.Orange,
        title: `${noteTitlesPrefix}.messaging`,
      }
    default:
      return { iconName: IconName.HelpCircle, iconColour: PairedColour.Navy, title: `${noteTitlesPrefix}.unknown` }
  }
}

export const getCheckboxesInitialState = (notes?: Maybe<Note[]>) =>
  Object.keys(FENoteType).map((noteType) => ({
    noteType: noteType as FENoteType,
    ...getNoteTypeDisplayDetails(noteType as FENoteType),
    checked: true,
    amount:
      notes?.reduce(
        (acc, { primary_type, communication_type }) =>
          beToFeNoteTypeMapping(primary_type, communication_type) === noteType ? acc + 1 : acc,
        0,
      ) || 0,
  }))

export enum CheckboxReducerActionType {
  ShowAll = 'showAll',
  Check = 'check',
  UpdateCount = 'updateCount',
}

export const checkboxReducer = (
  state: ReturnType<typeof getCheckboxesInitialState>,
  action:
    | { type: CheckboxReducerActionType.Check; payload: FENoteType }
    | { type: CheckboxReducerActionType.ShowAll; payload?: never }
    | { type: CheckboxReducerActionType.UpdateCount; payload: Note[] },
) => {
  switch (action.type) {
    case CheckboxReducerActionType.ShowAll:
      return state.map((props) => ({ ...props, checked: props.amount > 0 }))
    case CheckboxReducerActionType.Check:
      return (() => {
        const isAllChecked = state.every((props) => (props.amount > 0 && props.checked) || props.amount === 0)
        if (isAllChecked) {
          return state.map((props) => ({ ...props, checked: props.noteType === action.payload ? true : false }))
        } else if (action.payload === FENoteType.Messaging) {
          return state.map(({ noteType, checked, ...props }) => ({
            ...props,
            noteType,
            checked: noteType === action.payload ? !checked : false,
          }))
        } else {
          return state.map(({ noteType, checked, ...props }) => {
            if (noteType === FENoteType.Messaging)
              return {
                ...props,
                noteType,
                checked: false,
              }
            return {
              ...props,
              noteType,
              checked: noteType === action.payload ? !checked : checked,
            }
          })
        }
      })()
    case CheckboxReducerActionType.UpdateCount:
      return state.map((props) => ({
        ...props,
        amount:
          action.payload?.reduce(
            (acc, { primary_type, communication_type }) =>
              beToFeNoteTypeMapping(primary_type, communication_type) === props.noteType ? acc + 1 : acc,
            0,
          ) || 0,
      }))
    default:
      return [...state]
  }
}

export const beToFeNoteTypeMapping = (
  primary_type?: Maybe<NoteType>,
  communication_type?: Maybe<CommunicationType>,
) => {
  switch (primary_type) {
    case 'NOTE':
      return FENoteType.General
    case 'CALENDAR':
      return FENoteType.Calendar
    case 'COMMUNICATION':
      switch (communication_type) {
        case 'SMS':
          return FENoteType.Sms
        case 'EMAIL':
          return FENoteType.Email
        case 'CALL':
          return FENoteType.Call
        case 'SECURE_CLIENT_COMMUNICATION':
          return FENoteType.Messaging
        default:
          return null
      }
    default:
      return null
  }
}

export const feToBeNoteTypeMapping = (
  noteType: FENoteType,
): { primary_type: NoteType; communication_type: CommunicationType | null } => {
  switch (noteType) {
    case FENoteType.General:
      return { primary_type: 'NOTE', communication_type: null }
    case FENoteType.Calendar:
      return { primary_type: 'CALENDAR', communication_type: null }
    case FENoteType.Email:
      return { primary_type: 'COMMUNICATION', communication_type: 'EMAIL' }
    case FENoteType.Sms:
      return { primary_type: 'COMMUNICATION', communication_type: 'SMS' }
    case FENoteType.Call:
      return { primary_type: 'COMMUNICATION', communication_type: 'CALL' }
    case FENoteType.Messaging:
      return { primary_type: 'COMMUNICATION', communication_type: 'SECURE_CLIENT_COMMUNICATION' }
  }
}

export const useAddNote = ({ orgId, caseId }: { orgId: string; caseId: string }) => {
  const updateOptions = {
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: GetNotesDocument,
        variables: {
          params: {
            organisation_id: orgId,
            filter_case_id: caseId,
          },
          hydrateCreator: true,
        },
      },
    ],
  }

  const [addNoteOp, { loading }] = useMutation<AddNoteMutation>(AddNoteDocument, {
    ...updateOptions,
    onError: (err) => ErrorProvider.showError(new Error(err.message)),
  })
  const addNote = (note: NoteInput) => addNoteOp({ variables: { input: note } })
  return { addNote, loading }
}

export const formatNoteInput = (note: Note | NoteInput): NoteInput => {
  const noteInput = omitTypename(note)

  return omit(noteInput, ['creator'])
}

export const useUpdateMessage = () => {
  const [updateMessageOp, { loading }] = useMutation<UpdateNoteMutation>(UpdateNoteDocument, {
    onError: (err) => ErrorProvider.showError(new Error(err.message)),
  })
  const updateMessage = (note: NoteInput) =>
    updateMessageOp({ variables: { note_id: note.note_id, input: formatNoteInput(note) } })
  return { updateMessage, loading }
}

// A note lives at the case owner's (AR) organisation level. A DA sees notes on cases it
// has compliance permissions over, so a note must only ever be tagged with the case
// owner's org — never the acting user's org or the DA (representative_of) org. Callers
// build `organisation_ids: [getOrgIdForCaseNotes(details)]` from this.
export const getOrgIdForCaseNotes = (details: Maybe<Case>) => {
  return details?.owner?.organisation?.id || details?.owner_organisation_id || details?.owner_organisation?.id || ''
}
